<template>
  <div class="el-main">
    <NewYagiofferEduHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :active-history-item="activeHistoryItem"
      :educational-reset-visible="educationalResetDialogVisible"
      :description-max-length="descriptionMaxLength"
      @set-educational-backgrounds="setEducationalBackgrounds"
      @update:educational-reset-visible="educationalResetDialogVisible = $event"
      @reset-educational-backgrounds="resetEducationalBackgrounds"
      @add-educational-history="addEducationalHistory"
      @delete-educational-history="deleteEducationalHistory"
      @move-educational-history="moveEducationalHistory"
      @activate-history-item="activateHistoryItem"
    />

    <NewYagiofferRequestsCard :current-card-id="currentCardId" />

    <div class="card" v-show="currentCardId == 'complete'">
      <DownloadItem
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import { useCareerYagiofferHistories } from '@/composables/format-edit/useCareerYagiofferHistories'
import {
  createEmptyNewYagiofferFormatData,
  provideNewYagiofferFormatData
} from '@/composables/format-edit/useNewYagiofferFormat'
import NewYagiofferEduHistoryCard from './NewYagiofferEduHistoryCard.vue'
import NewYagiofferRequestsCard from './NewYagiofferRequestsCard.vue'
import DownloadItem from '@/components/formats/CommonParts/YagiofferDownloadItem.vue'

const props = defineProps<{
  basicData: Record<string, unknown>
  formatId?: string
  previewFormat?: { errorMessages?: Record<string, string> } | null
  currentCardId?: string
  exampleSentences?: Record<string, unknown>
}>()

const { formLib } = useHostServices()

const { formatData } = useFormatLocalData({
  saveName: 'rirekisho',
  saveOtherName: 'shokumukeirekisho',
  createEmpty: createEmptyNewYagiofferFormatData
})
provideNewYagiofferFormatData(formatData)

const {
  activeHistoryItem,
  educationalResetDialogVisible,
  setEducationalBackgrounds,
  resetEducationalBackgrounds,
  addEducationalHistory,
  deleteEducationalHistory,
  moveEducationalHistory,
  activateHistoryItem
} = useCareerYagiofferHistories(formatData)

const descriptionMaxLength = 80
const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiPostEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)
const downloadActiveCollapses = ref(['postcard'])
const isSendedYagishIntern = ref(false)

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

function setIsSendedYagishIntern(rslt: boolean) {
  isSendedYagishIntern.value = rslt
}

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = formLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  formatData.registYagioffer = true
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
  emitter.on('set_is_sendedYagishIntern', setIsSendedYagishIntern)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
  emitter.off('set_is_sendedYagishIntern', setIsSendedYagishIntern)
})
</script>

<style lang="scss" scoped>
.yagitaku {
  line-height: 25px;
  padding: 15px 0;
  font-weight: bold;
}

.yagitaku_recommend {
  color: #fff;
  background: #e3165c;
  padding: 5px 10px;
  margin-right: 5px;
  border-radius: 50px;
}

.yagitaku_link {
  color: #e3165c;
  border-bottom: 1px solid #e3165c;
}

.create-date_bnr {
  margin: 15px 0;
}

.el-button--success.overwrite.overwrite,
.el-button--primary.overwrite {
  color: #000;
  background: #eff8f8;
  border: none;
  display: block;
  padding: 0;
  border-radius: 15px;
  white-space: normal;
  line-height: 1.8;
}

.el-button--success.overwrite.overwrite:hover,
.el-button--primary.overwrite:hover {
  background: none;
  color: #000;
}

.addition {
  font-size: 0.75rem;
  margin: 0.5rem 0;
}

* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.next_bnr {
  font-size: 1rem;
  color: #004d40;
  text-align: center;
  padding: 1.2rem;
}

.bnr_container {
  display: flex;
  flex-wrap: wrap;
  margin: calc(-30px / 2);
  padding: 30px;
}

.bnr_itemWrapper {
  width: calc(100% / 2 - 30px);
  margin: calc(30px / 2);
}

.bnr_item {
  text-align: center;
}

.bnr_item img {
  border-radius: 10px 10px 0 0;
}

.bnr_txt {
  padding: 15px;
  background: #eff8f8;
  border-radius: 0 0 10px 10px;
}

.txt {
  border-radius: 10px;
}

.bnr_txt p {
  font-size: 0.8rem;
  font-weight: bold;
}

.bnr_txt h3 {
  color: #e3165c;
  font-size: 1.1rem;
  font-weight: bold;
}

.bnr_txt span {
  font-size: 0.8rem;
}

.create-date_txt {
  border-radius: 10px;
}

.bnr_note {
  font-size: 0.8rem;
}

.card-header h1 {
  padding-bottom: 1.5rem;
}

.card-header h2 {
  padding-bottom: 1.5rem;
}

/* タブレット 2カラム */
@media screen and (width <= 1024px) {
  .bnr_itemWrapper {
    width: calc(100% / 2 - 30px);
  }
}

/* スマホ 1カラム */
@media screen and (width <= 599px) {
  .bnr_itemWrapper {
    width: calc(100% / 1 - 30px);
  }
}
</style>
